import { BLOCKS } from "@contentful/rich-text-types";
import { Box, SxProps, Typography } from "@mui/material";
import { routes } from "../../constants/routes";
import { MarketingClaim } from "../../models/schema";
import { colors } from "../../theme/colors";
import { AnimatedView } from "../utils/AnimatedView";
import NavButton from "../utils/NavButton";
import RenderContent from "../utils/RenderContent";

export type ResponsiveBreakpoint = "XS" | "SM" | "MD" | "LG" | "XL" | "ALL";

type Props = {
  id: string;
  title: string;
  titlePrefix?: string;
  marketingClaims?: MarketingClaim[];
  actionLabel: string;
  zIndex?: number;
  backgroundUrlXS: string;
  backgroundUrlSM: string;
  backgroundUrlMD?: string;
  backgroundUrlLG?: string;
  backgroundUrlXL?: string;
  orientation?: "left" | "right";
  colorInvert?: Array<ResponsiveBreakpoint>;
  sx?: SxProps;
};

const OUR_SOLUTIONS_ID = "nase-riesenia";

export const HomeProject = ({
  id,
  title,
  titlePrefix,
  marketingClaims,
  actionLabel,
  orientation = "left",
  colorInvert,
  zIndex = 0,
  backgroundUrlXS,
  backgroundUrlSM,
  backgroundUrlMD,
  backgroundUrlLG,
  backgroundUrlXL,
  sx = {}
}: Props) => {
  const getImage = (size: ResponsiveBreakpoint) => {
    switch (size) {
      case "XS":
        return backgroundUrlXS;
      case "SM":
        return backgroundUrlSM || backgroundUrlXS;
      case "MD":
        return backgroundUrlMD || backgroundUrlSM || backgroundUrlXS;
      case "LG":
        return backgroundUrlLG || backgroundUrlMD || backgroundUrlSM || backgroundUrlXS;
      case "XL":
        return (
          backgroundUrlXL ||
          backgroundUrlLG ||
          backgroundUrlMD ||
          backgroundUrlSM ||
          backgroundUrlXS
        );
    }
  };

  const marketingClaim: MarketingClaim = marketingClaims?.[0] as MarketingClaim;
  const route = id === OUR_SOLUTIONS_ID ? routes.solutions : `${routes.project}/${id}`;

  const isColorInvertActive = (resolution: ResponsiveBreakpoint) => {
    const isColorInvertActive = colorInvert?.includes(resolution) || colorInvert?.includes("ALL");
    return isColorInvertActive;
  };

  return (
    <AnimatedView sx={{ zIndex, position: "relative" }} animation="fadeIn">
      <Box
        sx={{
          width: "100%",
          maxHeight: 1200,
          minHeight: { xs: 800, sm: 1000, md: 800 },
          aspectRatio: "2 / 1",
          backgroundImage: {
            xs: `url(${getImage("XS")})`,
            sm: `url(${getImage("SM")})`,
            md: `url(${getImage("MD")})`,
            lg: `url(${getImage("LG")})`,
            xl: `url(${getImage("XL")})`
          },
          backgroundPosition: "bottom",
          borderRadius: "100% 0% 100% 0% / 0% 80% 5% 100%",
          mb: { xs: -10, xl: -15 },
          pt: { xs: 15, md: 20 },
          pb: 10,
          backgroundSize: "cover",
          display: "flex",
          justifyContent: "center",
          alignItems: { xs: "start", md: "center" },
          ...sx
        }}>
        <Box
          sx={{
            width: "100%",
            padding: 5,
            maxWidth: 1400,
            alignItems: { xs: "start", md: orientation === "left" ? "start" : "end" },
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            color: {
              xs: isColorInvertActive("XS") ? "white" : colors.grey5,
              sm: isColorInvertActive("SM") ? "white" : colors.grey5,
              md: isColorInvertActive("MD") ? "white" : colors.grey5,
              lg: isColorInvertActive("LG") ? "white" : colors.grey5,
              xl: isColorInvertActive("XL") ? "white" : colors.grey5
            }
          }}>
          <Box
            sx={{
              justifyContent: "start",
              maxWidth: { xs: "100%", md: "40%" },
              display: "flex",
              flexDirection: "column",
              color: {
                xs: isColorInvertActive("XS") ? "white" : colors.grey5,
                sm: isColorInvertActive("SM") ? "white" : colors.grey5,
                md: isColorInvertActive("MD") ? "white" : colors.grey5,
                lg: isColorInvertActive("LG") ? "white" : colors.grey5,
                xl: isColorInvertActive("XL") ? "white" : colors.grey5
              }
            }}>
            <Typography fontSize={15} mb={{ xs: 5, md: 10 }}>
              {id !== OUR_SOLUTIONS_ID && titlePrefix}
              <strong>{title}</strong>
            </Typography>
            <Typography
              variant="h1"
              fontSize={{ xs: 40, md: 60 }}
              color={colors.yellow}
              sx={{
                px: { xs: 4, md: 5 },
                my: 5,
                borderBottomRightRadius: { xs: 20, md: 30 },
                backgroundColor: colors.grey4,
                maxWidth: "fit-content"
              }}>
              {marketingClaim?.number}
            </Typography>
            <RenderContent
              content={marketingClaim?.text?.json}
              options={{
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => (
                    <Typography variant="h3" fontWeight="normal">
                      {children}
                    </Typography>
                  )
                }
              }}
            />
            <NavButton sx={{ mt: 5 }} variant="primary" label={actionLabel} route={route} />
          </Box>
        </Box>
      </Box>
    </AnimatedView>
  );
};
